/**
 * Storyline client facade — single entry point UI / hooks use for Storyline ops
 * (storyline-design.md §0.2 "数据层单接缝"). Wraps the real `@amzn/ivthub-client`
 * Smithy commands so pages/components never散调 `getIvtClient().send(...)`;接新 op
 * 只动这里。
 *
 * Storyline = per-industry narrative grouping (Industry → Storyline → Asset),
 * orthogonal to UC (solutionMapLinks) and Topic (topicLinks). id is a v4 UUID
 * (server-generated). See storyline.smithy for the authoritative shapes.
 *
 * Returning non-null-narrowed results (`?? []` / stripping `| undefined`) lets call
 * sites keep clean narrowing — the SDK marks `@required` fields as `| undefined`
 * because Smithy required is wire-shape-only. VITE_IVT_MOCK=1 routes getIvtClient()
 * to MockIvtHubClient (local dev), same as every other IVT facade.
 */

import {
  getIvtClient,
  ListStorylinesCommand,
  GetStorylineCommand,
  CreateStorylineCommand,
  UpdateStorylineCommand,
  UpdateStorylineDescriptionCommand,
  DeleteStorylineCommand,
  LinkStorylineAssetCommand,
  UnlinkStorylineAssetCommand,
  type Storyline,
  type StorylineSummary,
} from './IvtHubClient';

export const storylineClient = {
  /** List storylines (optionally one industry). Overview Menu tree + Create/Edit
   *  modal + Asset detail multiselect data source. Not paginated (bounded set). */
  async list(industry?: string): Promise<StorylineSummary[]> {
    const out = await getIvtClient().send(new ListStorylinesCommand(industry ? { industry } : {}));
    return out.items ?? [];
  },

  /** Get one storyline (detail page). Carries linkedAssets so the detail page's
   *  asset list renders without a follow-up ListAssets. */
  async get(storylineId: string): Promise<Storyline | undefined> {
    const out = await getIvtClient().send(new GetStorylineCommand({ storylineId }));
    return out.storyline;
  },

  /** Create a storyline (Create modal). id is server-generated. Optionally
   *  batch-links assets in the same transaction. */
  async create(input: {
    name: string;
    industry: string;
    description?: string;
    assetIds?: string[];
  }): Promise<Storyline | undefined> {
    const out = await getIvtClient().send(new CreateStorylineCommand(input));
    return out.storyline;
  },

  /** Update name and/or the linked-asset set (Edit modal). SET semantics: assetIds
   *  absent = links untouched, [] = clear all, [..] = replace exactly (backend diffs).
   *  Pass only the fields that changed. `description` is NOT updated here — use
   *  updateDescription (dedicated op). */
  async update(input: { storylineId: string; name?: string; assetIds?: string[] }): Promise<Storyline | undefined> {
    const out = await getIvtClient().send(new UpdateStorylineCommand(input));
    return out.storyline;
  },

  /** Update only the description (detail page inline ✎ edit). `""` clears it. */
  async updateDescription(storylineId: string, description: string): Promise<Storyline | undefined> {
    const out = await getIvtClient().send(new UpdateStorylineDescriptionCommand({ storylineId, description }));
    return out.storyline;
  },

  /** Delete a storyline. Cascades storyline_asset_link rows (backend FK CASCADE). */
  async remove(storylineId: string): Promise<void> {
    await getIvtClient().send(new DeleteStorylineCommand({ storylineId }));
  },

  /** Link an asset to a storyline (Asset detail multiselect). Idempotent. Backend
   *  rejects if asset.industry != storyline.industry (InvalidInputException). */
  async linkAsset(assetId: string, storylineId: string): Promise<void> {
    await getIvtClient().send(new LinkStorylineAssetCommand({ assetId, storylineId }));
  },

  /** Unlink an asset from a storyline. Idempotent. */
  async unlinkAsset(assetId: string, storylineId: string): Promise<void> {
    await getIvtClient().send(new UnlinkStorylineAssetCommand({ assetId, storylineId }));
  },
};
